import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, CircularProgress, Collapse } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AuthContext } from '../../../context/auth-context';
import ProductionMetricsAPI from '../../../../services/api/analytics/production-metrics-api';
import PackageTable from '../components/PackageTable';
import WorkPackageProgressTable from '../components/WorkPackageProgressTable';

// Props that parameterise PackageTable for shop vs field sections
const LIST_PROPS = {
  in_progress_shop: {
    pctKey: 'shopPercentComplete',
    budgetKey: 'shopBudgetHrs',
    actualKey: 'shopActualHrs',
    updatedAtKey: 'shopPctUpdatedAt',
    updatedByKey: 'shopPctUpdatedBy',
    patchSource: 'shop',
    isField: false,
  },
  in_progress_field: {
    pctKey: 'fieldPercentComplete',
    budgetKey: 'fieldBudgetHrs',
    actualKey: 'fieldActualHrs',
    updatedAtKey: 'fieldPctUpdatedAt',
    updatedByKey: 'fieldPctUpdatedBy',
    patchSource: 'field',
    isField: true,
  },
};

const SIMPLE_TABLE_PROPS = {
  pre_execution: {
    showPlannedDates: true,
  },
  ready_for_field: {
    showPlannedDates: true,
  },
  completed: {
    showActualHours: true,
    showVariance: true,
  },
  archived: {
    showActualHours: true,
    showVariance: true,
  },
};

const GRID_COLS = '18px minmax(220px, 1fr) 76px 92px 92px 124px 58px';

const formatHours = (value) => `${Math.round(value || 0).toLocaleString()} hrs`;

/**
 * Work Package Progress tab.
 *
 * Work Package Progress summary board:
 * - Proportional progress bar + color legend
 * - Column headers: STATUS, PACKAGES, SHOP HRS, FIELD HRS, TOTAL BUDGET HRS, % TOTAL
 * - Six collapsible sections in the backend-provided fixed order
 * - PackageTable inside in-progress shop / field sections
 * - WorkPackageProgressTable inside pre-execution, ready, completed, and archive sections
 * - Total row at bottom
 */
const WorkPackagesTab = ({
  projectId,
  packagesData,
  loadingPackages,
  packagesError,
  filterAreaName,
  filterPmId,
  filterSearchText,
}) => {
  const [expanded, setExpanded] = useState({
    pre_execution: false,
    in_progress_shop: false,
    ready_for_field: false,
    in_progress_field: false,
    completed: false,
    archived: false,
  });

  // Client-side packages filtering & proportional progress recalculation
  const filteredData = React.useMemo(() => {
    if (!packagesData) return null;

    const { categories, totalBudgetHrs, grandTotal } = packagesData;
    if (!categories) return null;

    // Check if any filter is active
    const hasFilter =
      Boolean(filterAreaName) ||
      Boolean(filterPmId) ||
      Boolean(filterSearchText.trim());

    if (!hasFilter) {
      return {
        categories,
        totalBudgetHrs,
        grandTotal,
      };
    }

    const filteredSearchLower = filterSearchText.trim().toLowerCase();

    // Map categories and filter packages
    const newCategories = categories.map((cat) => {
      if (cat.packages) {
        const filteredPackages = cat.packages.filter((pkg) => {
          // Area filter
          if (filterAreaName) {
            if (
              !pkg.areaName ||
              pkg.areaName.toLowerCase() !== filterAreaName.toLowerCase()
            ) {
              return false;
            }
          }
          // PM filter
          if (filterPmId) {
            if (pkg.pmEmployeeId !== filterPmId) {
              return false;
            }
          }
          // Search contains
          if (filteredSearchLower) {
            const combined =
              `${pkg.autodeskName || ''} ${pkg.name || ''}`.toLowerCase();
            if (!combined.includes(filteredSearchLower)) {
              return false;
            }
          }
          return true;
        });

        const catShopBudget = filteredPackages.reduce(
          (sum, p) =>
            sum + (parseFloat(p.shopBudgetHrs || p.shopHrsBudgeted) || 0),
          0
        );
        const catFieldBudget = filteredPackages.reduce(
          (sum, p) =>
            sum + (parseFloat(p.fieldBudgetHrs || p.fieldHrsBudgeted) || 0),
          0
        );
        const catTotalBudget = catShopBudget + catFieldBudget;

        return {
          ...cat,
          packageCount: filteredPackages.length,
          shopHrsBudgeted: catShopBudget,
          fieldHrsBudgeted: catFieldBudget,
          totalHrsBudgeted: catTotalBudget,
          packages: filteredPackages,
        };
      }

      return {
        ...cat,
        packageCount: 0,
        shopHrsBudgeted: 0,
        fieldHrsBudgeted: 0,
        totalHrsBudgeted: 0,
        packages: [],
      };
    });

    const newGrandTotal = newCategories.reduce(
      (total, bucket) => {
        total.packageCount += bucket.packageCount;
        total.shopHrsBudgeted += bucket.shopHrsBudgeted;
        total.fieldHrsBudgeted += bucket.fieldHrsBudgeted;
        total.totalHrsBudgeted += bucket.totalHrsBudgeted;
        return total;
      },
      {
        packageCount: 0,
        shopHrsBudgeted: 0,
        fieldHrsBudgeted: 0,
        totalHrsBudgeted: 0,
      }
    );

    const newTotalHrs = newGrandTotal.totalHrsBudgeted;

    newGrandTotal.shopHrsPct =
      newTotalHrs > 0
        ? Math.round((newGrandTotal.shopHrsBudgeted / newTotalHrs) * 1000) / 10
        : 0;
    newGrandTotal.fieldHrsPct =
      newTotalHrs > 0
        ? Math.round((newGrandTotal.fieldHrsBudgeted / newTotalHrs) * 1000) / 10
        : 0;

    // Recalculate percentOfTotal for progress bar and list display
    const finalCategories = newCategories.map((c) => ({
      ...c,
      percentOfTotal:
        newTotalHrs > 0
          ? Math.round((c.totalHrsBudgeted / newTotalHrs) * 1000) / 10
          : 0,
    }));

    return {
      categories: finalCategories,
      grandTotal: newGrandTotal,
      totalBudgetHrs: newTotalHrs,
    };
  }, [packagesData, filterAreaName, filterPmId, filterSearchText]);

  // ── Guards ─────────────────────────────────────────────────────────────

  if (!projectId) {
    return (
      <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
        Select a project to view work package progress.
      </Typography>
    );
  }

  if (loadingPackages) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (packagesError) {
    return (
      <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
        {packagesError}
      </Typography>
    );
  }

  if (!filteredData) return null;

  const { categories = [], grandTotal = {} } = filteredData;
  const {
    packageCount = 0,
    shopHrsBudgeted = 0,
    shopHrsPct = 0,
    fieldHrsBudgeted = 0,
    fieldHrsPct = 0,
    totalHrsBudgeted = filteredData.totalBudgetHrs || 0,
  } = grandTotal;

  const toggleSection = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Box>
      {/* ── Card container ──────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '0.5px solid',
          borderColor: 'divider',
          p: 2.5,
        }}
      >
        {/* ── Section title ───────────────────────────────────────── */}
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1.5,
          }}
        >
          Work Package Progress
        </Typography>

        {/* ── Progress bar ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            height: 9,
            borderRadius: 1,
            overflow: 'hidden',
            mb: 0.75,
          }}
        >
          {categories.map(
            (cat) =>
              cat.percentOfTotal > 0 && (
                <Box
                  key={cat.id}
                  sx={{
                    width: `${cat.percentOfTotal}%`,
                    bgcolor: cat.color,
                    transition: 'width 0.4s',
                  }}
                />
              )
          )}
        </Box>

        {/* ── Legend ──────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          {categories.map((cat) => (
            <Box
              key={cat.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '2px',
                  bgcolor: cat.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                {cat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── Column headers ───────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            gap: 1,
            px: 0.75,
            pb: 0.75,
            borderBottom: '0.5px solid',
            borderColor: 'divider',
          }}
        >
          {[
            '',
            'Status',
            'Packages',
            'Shop hrs',
            'Field hrs',
            'Total budget hrs',
            '% total',
          ].map((h, i) => (
            <Typography
              key={i}
              sx={{
                fontSize: '10px',
                fontWeight: 500,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                textAlign: i > 1 ? 'right' : 'left',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* ── Section rows ───────────────────────────────────────── */}
        {categories.map((cat) => {
          const isOpen = expanded[cat.id];
          const hasPackages = cat.packages && cat.packages.length > 0;
          const listProps = LIST_PROPS[cat.id];
          const simpleTableProps = SIMPLE_TABLE_PROPS[cat.id];

          return (
            <Box key={cat.id}>
              {/* Header row — grid layout matching column headers */}
              <Box
                onClick={() => toggleSection(cat.id)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLS,
                  gap: 1,
                  alignItems: 'center',
                  px: 0.75,
                  py: 1.25,
                  cursor: 'pointer',
                  borderBottom: '0.5px solid',
                  borderColor: 'divider',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* Chevron */}
                <ChevronRightIcon
                  sx={{
                    fontSize: 14,
                    color: 'text.secondary',
                    transform: isOpen ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.18s',
                  }}
                />

                {/* Color dot + Label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '2px',
                      bgcolor: cat.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {cat.label}
                  </Typography>
                </Box>

                {/* Stats — right-aligned to match column headers */}
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'text.secondary',
                    textAlign: 'right',
                  }}
                >
                  {(cat.packageCount || 0).toLocaleString()} pkgs
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    textAlign: 'right',
                  }}
                >
                  {formatHours(cat.shopHrsBudgeted)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    textAlign: 'right',
                  }}
                >
                  {formatHours(cat.fieldHrsBudgeted)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    textAlign: 'right',
                  }}
                >
                  {formatHours(cat.totalHrsBudgeted)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'text.secondary',
                    textAlign: 'right',
                  }}
                >
                  {cat.percentOfTotal}%
                </Typography>
              </Box>

              {/* Expanded content */}
              <Collapse in={isOpen}>
                {hasPackages && listProps ? (
                  <Box
                    sx={{
                      borderBottom: '0.5px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <PackageTable packages={cat.packages} {...listProps} />
                  </Box>
                ) : hasPackages && simpleTableProps ? (
                  <Box
                    sx={{
                      borderBottom: '0.5px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <WorkPackageProgressTable
                      packages={cat.packages}
                      {...simpleTableProps}
                    />
                  </Box>
                ) : isOpen && !hasPackages ? (
                  <Box
                    sx={{
                      pl: 3.5,
                      py: 1.25,
                      borderBottom: '0.5px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', color: 'text.secondary' }}
                    >
                      No packages in this category.
                    </Typography>
                  </Box>
                ) : null}
              </Collapse>
            </Box>
          );
        })}

        {/* ── Total row ──────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            gap: 1,
            px: 0.75,
            py: 1.25,
            bgcolor: 'action.hover',
            borderRadius: 1,
            mt: 1,
          }}
        >
          <span />
          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
            Total
          </Typography>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}
          >
            {packageCount.toLocaleString()} pkgs
          </Typography>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}
          >
            {formatHours(shopHrsBudgeted)}
            <Typography
              component="span"
              sx={{
                display: 'block',
                fontSize: '10px',
                color: 'text.secondary',
              }}
            >
              {shopHrsPct}%
            </Typography>
          </Typography>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}
          >
            {formatHours(fieldHrsBudgeted)}
            <Typography
              component="span"
              sx={{
                display: 'block',
                fontSize: '10px',
                color: 'text.secondary',
              }}
            >
              {fieldHrsPct}%
            </Typography>
          </Typography>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}
          >
            {formatHours(totalHrsBudgeted)}
          </Typography>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}
          >
            100%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkPackagesTab;
